<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  channelName: '',
  agentId: '',
  apiKey: '',
  phoneNumber: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  channelName: { required },
  agentId: { required },
  apiKey: { required },
  phoneNumber: {},
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  channelName: v$.value.channelName?.$error
    ? t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.CHANNEL_NAME.ERROR')
    : '',
  agentId: v$.value.agentId?.$error
    ? t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.AGENT_ID.ERROR')
    : '',
  apiKey: v$.value.apiKey?.$error
    ? t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.API_KEY.ERROR')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createElevenlabsVoiceChannel', {
      name: state.channelName,
      agent_id: state.agentId,
      api_key: state.apiKey,
      phone_number: state.phoneNumber,
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.channelName"
        :label="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.CHANNEL_NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.CHANNEL_NAME.PLACEHOLDER')"
        :message="formErrors.channelName"
        :message-type="formErrors.channelName ? 'error' : 'info'"
        @blur="v$.channelName?.$touch"
      />

      <Input
        v-model="state.agentId"
        :label="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.AGENT_ID.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.AGENT_ID.PLACEHOLDER')"
        :message="formErrors.agentId"
        :message-type="formErrors.agentId ? 'error' : 'info'"
        @blur="v$.agentId?.$touch"
      />

      <Input
        v-model="state.apiKey"
        type="password"
        :label="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.API_KEY.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.API_KEY.PLACEHOLDER')"
        :message="formErrors.apiKey"
        :message-type="formErrors.apiKey ? 'error' : 'info'"
        @blur="v$.apiKey?.$touch"
      />

      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.PHONE_NUMBER.HELP')"
        message-type="info"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.ELEVENLABS_VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
